import React, { useEffect, useState, CSSProperties } from 'react';
import clsx from 'clsx';
import { useResizeDetector } from 'react-resize-detector';
import { ITabs } from '../../types/customizer';
import './index.scss';

import { twMerge } from 'tailwind-merge';
import { FONT_MAP, getSelectedInfo } from '../../utils/commonRingUtils';
import If from '../If';
import URLs from '../../utils/url';
import { isRightSidebar } from '../../utils/basic';

interface Props {
  pages: ITabs;
  selectedTab: string;
  handleActiveTab: Function;
  price: string;
  handleDone: () => void;
  pathPrefix?: string;
}

const DesktopSidebar = ({ pages, selectedTab, handleActiveTab, price, handleDone, pathPrefix }: Props) => {
  const { height, ref } = useResizeDetector();
  const [horizontalStyle, setHorizontalStyle] = useState<CSSProperties>({});
  const [verticalStyle, setVerticalStyle] = useState<CSSProperties>({});

  const isHelzberg = pathPrefix === 'helzberg';

  useEffect(() => {
    /**
     * Initial load it detects height 50px instead 60px due to data not rendered.
     * If vertical style, wait for 100ms
     * else hit directly
     */
    setTimeout(
      () => {
        setHorizontalStyle(getHorizontalStyle());
        setVerticalStyle(getVerticalStyle());
      },
      Object.keys(verticalStyle).length ? 0 : 100
    );
  }, [selectedTab, pages, height]);

  const getHorizontalStyle = (): CSSProperties => {
    const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
    const sidebarRect = document.getElementById(selectedTab)?.getBoundingClientRect();
    if (tabContentRect && sidebarRect) {
      let tabcontentTop = tabContentRect.top;
      const sidebarTopX = sidebarRect.top;
      const sidebarBottomX = sidebarRect.top + sidebarRect.height;

      const selectedPage = pages.find((item) => item.eventKey === selectedTab);
      const hasItems = selectedPage?.items?.length || 0;

      tabcontentTop = tabcontentTop + (hasItems ? 47 : 2);

      if (tabcontentTop - sidebarTopX > 20 && tabcontentTop - sidebarBottomX > 20) {
        // If side tab above 20px of margin line
        return {
          bottom: '0px',
          top: 'unset'
        };
      } else if (sidebarTopX - tabcontentTop > 20 && sidebarBottomX - tabcontentTop > 20) {
        // If side tab below 20px of margin line
        return {
          top: '0px',
          bottom: 'unset'
        };
      } else if (tabcontentTop - sidebarTopX > 20) {
        // If sidebar top above 20px of margin line
        return {
          top: '0px',
          bottom: 'unset'
        };
      } else {
        return {
          bottom: '0px',
          top: 'unset'
        };
      }
    }
    return {};
  };

  const getVerticalStyle = (): CSSProperties => {
    const tabContentRect = document.getElementById('main-content')?.getBoundingClientRect();
    const sidebarRect = document.getElementById(selectedTab)?.getBoundingClientRect();
    if (tabContentRect && sidebarRect) {
      let tabcontentTop = tabContentRect.top;
      const sidebarTopX = sidebarRect.top;
      const sidebarBottomX = sidebarRect.top + sidebarRect.height;

      const selectedPage = pages.find((item) => item.eventKey === selectedTab);
      const hasItems = selectedPage?.items?.length || 0;

      tabcontentTop = tabcontentTop + (hasItems ? 38 : 2);

      let height = 0;
      if (tabcontentTop - sidebarTopX > 5 && tabcontentTop - sidebarBottomX > 5) {
        // If side tab above 20px of margin line
        height = tabcontentTop - sidebarBottomX;
        return {
          height: height + 'px',
          top: '100%',
          bottom: 'unset'
        };
      } else if (sidebarTopX - tabcontentTop > 5 && sidebarBottomX - tabcontentTop > 5) {
        // If side tab below 20px of margin line
        height = sidebarTopX - tabcontentTop;
        return {
          height: height + 'px',
          bottom: '100%',
          top: 'unset'
        };
      } else if (tabcontentTop - sidebarTopX > 5) {
        // If sidebar top above 20px of margin line
        height = tabcontentTop - sidebarTopX;
        return {
          height: height + 'px',
          top: '0px',
          bottom: 'unset'
        };
      } else {
        height = sidebarBottomX - tabcontentTop;
        return {
          height: height + 'px',
          top: 'unset',
          bottom: '0px'
        };
      }
    }
    return {};
  };

  const getUrl = (swatch_url: string) => {
    return swatch_url.indexOf('http') !== -1 ? swatch_url : `${process.env.REACT_APP_ASSET_URL}${swatch_url}`;
  };

  const isRight = isRightSidebar(pathPrefix || '');
  // Hide for xs, sm, md devices
  return (
    <div
      ref={ref}
      className={clsx(
        'tw-hidden lg:tw-flex tw-bg-primary-bg-color desktop-sidebar tw-pb-3 tw-pt-2 tw-w-full tw-h-full tw-overflow-y-auto tw-overflow-x-hidden tw-grow-0 tw-shrink-0 lg:tw-basis-[280px] xl:tw-basis-[320px]',
        { left: !isRight, right: isRight }
      )}
    >
      {' '}
      {/* flex-basis and width both are same/ just used for advanced setup */}
      <div className="tw-flex tw-flex-col tw-justify-between tw-w-full">
        <div className="tw-flex tw-flex-col tw-w-full sidebar-tabs tw-mb-5">
          <p className="tw-mb-6 tw-mt-5 tw-text-sm tw-text-center">Powered by Lashbrook Builder</p>
          {pages.map((item) => {
            const selectedInfo = getSelectedInfo(item);
            const selectedText = selectedInfo?.text || '';

            return (
              <div
                id={item.eventKey}
                key={item.eventKey}
                className={clsx(
                  `tw-flex tw-items-center tw-justify-between lg:tw-text-base tw-cursor-pointer tw-py-2 tw-border-gray-500 sidebar-tab tw-border-solid`,
                  {
                    active: selectedTab === item.eventKey,
                    'in-active': selectedTab !== item.eventKey
                  }
                )}
                onClick={handleActiveTab?.(item.eventKey, false)}
              >
                <div className="tw-flex tw-items-start tw-justify-center tw-flex-col tab-text">
                  {/* Sidebar title starts */}
                  <p className={'tw-relative tw-font-medium sidebar-title primary-font-family'}>
                    {typeof item.title === 'string' && item.title}
                    {typeof item.title === 'function' && item.title()}
                  </p>
                  {/* Sidebar title ends */}

                  {/* Sub content starts */}
                  <div>
                    {item?.disclaimer && (
                      <p className="tw-text-xs tw-mt-2 tw-text-primary-color tw-text-opacity-80">{item?.disclaimer}</p>
                    )}
                    {/* For engraving alone */}
                    <If condition={item.eventKey === 'engraving' && item.selectedFeature === 'text'}>
                      <p className={twMerge('tw-text-xs tw-mt-2', FONT_MAP[selectedText || ''])}>
                        {selectedInfo?.engravingText || '(none)'}
                      </p>
                    </If>
                    {/* For outside and 2nd outside alone */}
                    <If
                      condition={
                        ['outsideFeature_1', 'outsideFeature_2'].includes(item.eventKey) && item.selectedFeature
                      }
                    >
                      <p className="tw-text-xs tw-mt-2">{item.selectedFeature}</p>
                    </If>
                  </div>
                  {/* Sub content ends */}
                </div>
                <div
                  className={clsx('tw-flex tw-justify-center tw-items-center tw-flex-col tw-w-24', {
                    'tw-relative tw-translate-y-[3px]': selectedTab === item.eventKey
                  })}
                >
                  {selectedInfo?.url && !/^None$/i.test(selectedText) && (
                    <img
                      className={clsx('tw-rounded-full tw-h-6 tw-w-6 tw-mb-1', {
                        'tw-ring-1 tw-ring-brand-color tw-ring-offset-4': selectedTab === item.eventKey
                      })}
                      alt="img"
                      src={`${getUrl(selectedInfo?.url)}`}
                    />
                  )}
                  {selectedText && (
                    <p
                      className={clsx('tw-text-xs tw-text-center tw-text-opacity-80', {
                        'tw-text-brand-color': selectedTab === item.eventKey,
                        'tw-text-primary-color': selectedTab !== item.eventKey
                      })}
                    >
                      {selectedText}
                    </p>
                  )}
                  <If condition={item.eventKey === 'summary' && !isHelzberg}>
                    <p className="tw-text-lg tw-text-center tw-text-brand-color">{price}</p>
                  </If>
                </div>
                <If condition={selectedTab === item.eventKey}>
                  {/* <div id="active-h-border" style={horizontalStyle}></div> */}
                  <div id="active-v-border" style={verticalStyle}></div>
                </If>
              </div>
            );
          })}

          <If condition={isHelzberg && pages.length}>
            <div
              className={clsx(
                'tw-flex tw-justify-between tw-items-center tw-py-2 tw-border-gray-500 tw-border-solid tw-border-t tw-ml-4 half-border-b tw-pl-1 tw-pr-3 tw-h-[50px]',
                {
                  'tw-border-t-0': selectedTab === 'summary'
                }
              )}
            >
              <p className="tw-font-medium sidebar-title primary-font-family">Estimated Total</p>
              <p className="tw-text-lg tw-text-center tw-text-brand-color">{price}</p>
            </div>
          </If>
        </div>

        <div className="tw-flex tw-items-center tw-justify-center tw-w-full tw-py-3">
          <button
            className="tw-bg-brand-color hover:tw-bg-brand-color tw-text-tertiary-color hover:tw-text-tertiary-color tw-w-full tw-max-w-[191px] tw-px-9 tw-py-2 tw-rounded-tertiary tw-font-medium tw-hidden lg:tw-block"
            onClick={handleDone}
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default DesktopSidebar;
